import { TFunction } from 'i18next';
import { FullScreenHandle } from 'react-full-screen';
import {
  DomainPreferences,
  MetricResult,
  SQLFilters,
} from '../../../../api/types';
import { Languages } from '../../../../constants';
import { GlobalDisplayState } from '../../../../context/types';
import {
  CohortMetricIdType,
  DataFieldWithDataType,
  EmployeeCohortMetricIdType,
  GroupBy,
  MetricIdType,
  MetricIdTypeArrays,
  RegularMetricIdType,
  SegmentBy,
  SingleValueTimeSelection,
  TimeSelection,
} from '../../../../types';
import { DataFieldValueFormatter } from '../../../hooks';
import {
  ManualDataRefetchCountHandle,
  ValidationResponse as ValidationResponseType,
} from '../../../types';
import { LanguageString } from '../../dashboards/types';
import { Filter, Segment } from '../../filter/filterbar/types';
import { ItemsState } from '../../filter/types';
import { TimeSliderState } from '../../timeslider/types';

export type UnsegmentedGroupVal = {
  groupValue: (string | null)[];
  total: number;
};

type SegmentVal = {
  segmentVal: string | null;
  value: number;
};

export type SegmentedGroupVal = {
  groupVal: string | null;
  segmentByDataField: DataFieldWithDataType;
  segmentVals: SegmentVal[];
};

export type UnsegmentedResponse = {
  metric: MetricIdType;
  isSegmented: false;
  groupBy: GroupBy;
  groups: UnsegmentedGroupVal[];
  metricSql: string | null;
};

export type SegmentedResponse = {
  metric: MetricIdType;
  isSegmented: true;
  groupBy: GroupBy;
  groups: SegmentedGroupVal[];
  metricSql: string | null;
};

export interface ExternalChartComponentProps {
  manualDataRefetchCountHandle: ManualDataRefetchCountHandle;
  // TODO: Don't think timeSlider, filters, segmentation etc should be in the
  // external props passed to the data viz component. These should be used to generate
  // the chart query, but that's it.
  timeSliderState: TimeSliderState | null;
  filtersState: ItemsState<Filter>;
  segmentationState: ItemsState<Segment>;
  preferences: DomainPreferences;
  displayGlobalState: GlobalDisplayState;
  fullScreenHandle: FullScreenHandle;
  locale: { selected: Languages };
}

export type DataOrError<T> =
  | {
      data: T;
      error: null;
    }
  | {
      data: null;
      error: Error;
    };

export type ChartTitleProps = {
  externalProps: ExternalChartComponentProps;
  metrics: MetricIdTypeArrays;
  filters: SQLFilters | undefined;
  timeSelection: TimeSelection;
  metricsSql: (string | null)[];
  segmentations: SegmentBy[];
  titleFromConfig?: LanguageString;
};

export interface ComponentStateManager<TState, TLocalActions> {
  state: TState;
  actions: TLocalActions;
}

export interface DataVizContainerCommonUtils {
  t: TFunction;
  dataFieldValueFormatter: DataFieldValueFormatter;
}

export type DataToUiComponentPropsWithStateManager<
  Data,
  UiComponentProps,
  ExternalProps = any,
  State = any,
  Actions = any,
> = (
  data: Data,
  externalProps: ExternalProps,
  componentStateManager: ComponentStateManager<State, Actions>,
  commonUtils: DataVizContainerCommonUtils
) => UiComponentProps;

export type DataToUiComponentPropsWithoutStateManager<
  Data,
  UiComponentProps,
  ExternalProps = any,
> = (
  data: Data,
  externalProps: ExternalProps,
  commonUtils: DataVizContainerCommonUtils
) => UiComponentProps;

export type DataVizContainerViewConfigWithoutStateManager<
  Data,
  ExternalProps,
  UiComponentProps extends object,
> = {
  hasStateManager: false;
  dataToUiComponentProps: DataToUiComponentPropsWithoutStateManager<
    Data,
    UiComponentProps,
    ExternalProps
  >;
  UiComponent: React.FC<UiComponentProps>;
};

export type DataVizContainerViewConfigWithStateManager<
  Data,
  ExternalProps,
  UiComponentProps extends object,
  State,
  Actions,
> = {
  hasStateManager: true;
  useStateManager: (
    externalProps: ExternalProps,
    persistenceKey: string
  ) => ComponentStateManager<State, Actions>;
  dataToUiComponentProps: DataToUiComponentPropsWithStateManager<
    Data,
    UiComponentProps,
    ExternalProps,
    State,
    Actions
  >;
  UiComponent: React.FC<UiComponentProps>;
};

export type ValidationResponse = ValidationResponseType;

export type QueryResponse =
  | { data: MetricResult[]; error: null }
  | { data: null; error: Error };

export interface MetricQueryConfig {
  metrics: MetricIdTypeArrays;
  timeSelection: TimeSelection;
  filters?: SQLFilters;
  segmentations?: SegmentBy[];
}

export interface UseMetricQueriesInputs {
  queries: MetricQueryConfig[];
  timeSliderState: TimeSliderState | null;
  filtersState: ItemsState<Filter>;
  segmentationState: ItemsState<Segment>;
}

export interface UseMultipleMetricsQueryInputs {
  metrics: MetricIdTypeArrays;
  timeSelection: TimeSelection;
  filters?: SQLFilters;
  segmentations?: SegmentBy[];
  timeSliderState: TimeSliderState | null;
  filtersState: ItemsState<Filter>;
  segmentationState: ItemsState<Segment>;
}

export enum BenchmarkTypes {
  VALUE = 'value',
  DELTA = 'delta',
  DELTA_PERCENT = 'deltaPercent',
}

export enum BenchmarkPeriods {
  PRIOR_PERIOD = 'priorPeriod',
  YEAR_ON_YEAR = 'yearOnYear',
}

export type DataVizContainerViewConfig<
  Data,
  ExternalProps,
  UiComponentProps extends object,
  State,
  Actions,
> =
  | DataVizContainerViewConfigWithoutStateManager<
      Data,
      ExternalProps,
      UiComponentProps
    >
  | DataVizContainerViewConfigWithStateManager<
      Data,
      ExternalProps,
      UiComponentProps,
      State,
      Actions
    >;

export interface DataVizContainerProps<
  DataConfig,
  ViewConfigNames,
  ExternalProps,
  Data,
  State,
  Actions,
> {
  dataConfig: DataConfig;
  selectedView: ViewConfigNames;
  externalProps: ExternalProps;
  useConfigToData: (
    dataConfig: DataConfig,
    externalProps: ExternalProps
  ) => DataOrError<Data>;
  viewConfigs: {
    name: ViewConfigNames;
    viewConfig: DataVizContainerViewConfig<
      Data,
      ExternalProps,
      any,
      State,
      Actions
    >;
  }[];
  persistenceKey: string; // TODO: Make this optional
}

export enum KpiFactboxBenchmarkTypes {
  VALUE = 'value',
  DELTA = 'delta',
  DELTA_PERCENT = 'deltaPercent',
}

export enum KpiFactboxBenchmarkPeriods {
  PRIOR_PERIOD = 'priorPeriod',
  YEAR_ON_YEAR = 'yearOnYear',
}

export interface KpiBenchmarkConfig {
  benchmarkTitle?: LanguageString;
  benchmarkPeriod: KpiFactboxBenchmarkPeriods;
  benchmarkType: KpiFactboxBenchmarkTypes;
}

export interface KpiSegmentationConfig {
  // default is true
  showMainValue?: boolean;
}

export interface KpiQueryConfig {
  metric: MetricIdType;
  timeSelection: SingleValueTimeSelection;
  filters?: SQLFilters;
  segmentBy?: SegmentBy;
}

export interface KpiFactboxSubQueryConfig {
  title?: LanguageString;
  query: Pick<KpiQueryConfig, 'metric' | 'timeSelection' | 'filters'>;
}

interface KpiComponentSectionConfigBase {
  title?: LanguageString;
  subtitle?: LanguageString;
  benchmarkConfigs?: KpiBenchmarkConfig[];
  segmentationConfig?: KpiSegmentationConfig;
}

interface KpiComponentSectionConfigWithQuery
  extends KpiComponentSectionConfigBase {
  query: KpiQueryConfig;
  subQueries?: KpiFactboxSubQueryConfig[];
}

interface KpiComponentSectionConfigWithSubQueries
  extends KpiComponentSectionConfigBase {
  query?: KpiQueryConfig;
  subQueries: KpiFactboxSubQueryConfig[];
}

/**
 * Section config for KPI components.
 * - If `subQueries` is provided, `query` becomes optional
 * - If `subQueries` is not provided, `query` is required
 */
export type KpiComponentSectionConfig =
  | KpiComponentSectionConfigWithQuery
  | KpiComponentSectionConfigWithSubQueries;

export interface KpiQueryMetaData {
  metric: RegularMetricIdType | CohortMetricIdType | EmployeeCohortMetricIdType;
  metricsSql: string;
}

export interface ValueWithQueryMetaData<T> {
  value: T;
  queryMetaData: KpiQueryMetaData | null;
}

export interface KpiFactboxBenchmarkValue {
  benchmarkType: KpiFactboxBenchmarkTypes;
  benchmarkPeriod: KpiFactboxBenchmarkPeriods;
  value: number | null;
  valueFormatter: (value: number) => string;
}

export interface KpiFactboxSegmentedValues {
  segmentBy: DataFieldWithDataType;
  values: {
    segmentVal: string | null;
    value: number | null;
  }[];
  valueFormatter: (value: number) => string;
}

export interface KpiFactboxSubValue {
  title: string;
  value: number | null;
  valueFormatter: (value: number) => string;
}

export interface KpiSectionValue {
  title?: string;
  subtitle?: string;
  mainValue: ValueWithQueryMetaData<{
    value: number | null;
    valueFormatter: (value: number) => string;
  }> | null;
  benchmarkValues: ValueWithQueryMetaData<KpiFactboxBenchmarkValue>[];
  segmentedValues: ValueWithQueryMetaData<KpiFactboxSegmentedValues> | null;
  subValues: ValueWithQueryMetaData<KpiFactboxSubValue>[];
}

export type OvertimeVizDatapointWithoutBenchmark = {
  hasBenchmark: false;
  value: number;
};

export type OvertimeVizDatapointWithBenchmark = {
  hasBenchmark: true;
  value: number;
  benchmarkData: {
    benchmarkDateLabel: string;
    value: number;
  };
};

export type OvertimeVizDatapoint<HasBenchmark extends boolean = any> =
  HasBenchmark extends true
    ? OvertimeVizDatapointWithBenchmark
    : OvertimeVizDatapointWithoutBenchmark;

export type TimePeriodTimeSelection = SingleValueTimeSelection;
